import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { SelectOption } from 'types/base';
import { useCallback, useMemo } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import {
  CreateProductFormSchemaType,
  generateNote,
  useGenerateDetailTitle,
  useMaterialCascaderOptions,
} from '../common';
import { FormLabel } from '../form-label';
import i18n from 'i18n';
import LOCALS from 'commons/locals';
import { Select } from 'antd';

const ProductHardware = ({
  control,
  setValue,
}: {
  control: Control<CreateProductFormSchemaType>;
  setValue: UseFormSetValue<CreateProductFormSchemaType>;
}) => {
  const { hardwareSelectOptions } = useAppSelector(selectGlobalInfo);

  const material = useWatch({
    control,
    name: 'material',
  });

  const { materialCascaderOptions } = useMaterialCascaderOptions(control);

  const hardwareListSort = useMemo((): SelectOption[] => {
    if (hardwareSelectOptions && hardwareSelectOptions.length) {
      return [...hardwareSelectOptions].sort(
        (a: SelectOption, b: SelectOption) => b.sort! - a.sort!
      );
    }
    return [];
  }, [hardwareSelectOptions]);

  const SelectFilter = useCallback(
    (input: string, option: SelectOption | undefined) =>
      (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
    []
  );

  const { generateDetailTitle } = useGenerateDetailTitle(control);

  return (
    <div className="flex items-center gap-2">
      <FormLabel>{i18n.t(LOCALS.hardware)}:</FormLabel>
      <Controller
        name="hardware"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            className="w-full"
            mode="multiple"
            options={hardwareListSort}
            showSearch
            filterOption={SelectFilter}
            placeholder={i18n.t(LOCALS.please_select) || ''}
            allowClear
            onChange={(value) => {
              field.onChange(value);
              const note = generateNote(
                material,
                value,
                materialCascaderOptions,
                hardwareSelectOptions
              );
              setValue('note', note);

              const detailTitle = generateDetailTitle({
                hardware: value,
              });
              setValue('detailTitle', detailTitle);
            }}
          />
        )}
      />
    </div>
  );
};

export default ProductHardware;
